/**
 * /finance/ap — finance admins: the AP queue (Ready for Export actuals) and Business Central export batches.
 * Export = one server transaction (finance_create_export_batch): it locks the rows, builds the CSV in the active
 * template's layout, stores the file + sha256 and marks the rows exported -- all or nothing. The download is the
 * stored file, byte for byte. Voiding a row (with a reason) puts it back in the queue for a new export.
 */
import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFinancePayeeNames } from "../../hooks/useFinancePayeeNames";
import Button from "../../components/common/Button";
import ExpenseDetailModal from "../../components/finance/ExpenseDetailModal";
import {
  emptyApExportState,
  exportSelection,
  retryDownload,
  type ApExportDeps,
  type ApExportState,
} from "./apExportFlow";
import { cleanDocumentSearch, documentRange } from "./bcDocumentNo";
import {
  createExportBatch,
  downloadBatch,
  exportPreview,
  financeErrorMessage,
  getFinanceLookups,
  listBatchItems,
  listExportBatches,
  listExpenses,
  listStaffDirectory,
  money,
  personName,
  searchBcDocument,
} from "../../services/finance.service";
import type { FinanceAccess, StaffPerson } from "../../types/finance.types";

export default function FinanceAP() {
  const access = useOutletContext<FinanceAccess>();
  const qc = useQueryClient();
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    csv: string;
    problems: Record<string, string[]>;
  } | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [batchOpen, setBatchOpen] = useState<string | null>(null);
  const [docSearch, setDocSearch] = useState("");
  const docQuery = cleanDocumentSearch(docSearch);
  // audit F2: export intents and pending downloads are tracked separately (apExportFlow.ts)
  const [flow, setFlow] = useState<ApExportState>(emptyApExportState);
  const flowDeps: ApExportDeps = {
    create: createExportBatch,
    download: downloadBatch,
    newToken: () => crypto.randomUUID(),
    describeError: (e) => {
      const m = financeErrorMessage(e);
      return m.startsWith("{") ? `Not exported: ${m}` : m;
    },
  };

  const queueQ = useQuery({
    queryKey: ["finance-expenses", "ap"],
    queryFn: () => listExpenses({ status: "ready_for_export" }),
  });
  const batchesQ = useQuery({
    queryKey: ["finance-batches"],
    queryFn: listExportBatches,
  });
  const itemsQ = useQuery({
    queryKey: ["finance-batch-items", batchOpen],
    queryFn: () => listBatchItems(batchOpen!),
    enabled: !!batchOpen,
  });
  const docSearchQ = useQuery({
    queryKey: ["finance-doc-search", docQuery],
    queryFn: () => searchBcDocument(docQuery),
    enabled: docQuery.length >= 3,
  });
  const batchById = useMemo(
    () => new Map((batchesQ.data ?? []).map((b) => [b.id, b])),
    [batchesQ.data],
  );
  const lookupsQ = useQuery({
    queryKey: ["finance-lookups"],
    queryFn: getFinanceLookups,
    staleTime: 60_000,
  });
  const peopleQ = useQuery({
    queryKey: ["finance-staff"],
    queryFn: listStaffDirectory,
    staleTime: 300_000,
  });
  const people = useMemo(
    () =>
      new Map<string, StaffPerson>((peopleQ.data ?? []).map((p) => [p.id, p])),
    [peopleQ.data],
  );
  const rows = queueQ.data ?? [];
  const payeeName = useFinancePayeeNames(rows.map((r) => r.payee_id));
  const selected = rows.filter((r) => sel.has(r.id));
  const total = selected.reduce((a, r) => a + Number(r.actual_amount ?? 0), 0);

  if (!access.is_finance_admin) {
    return (
      <p className="text-sm text-gray-600">
        The AP queue is for finance administrators.
      </p>
    );
  }

  const toggle = (id: string) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) {
        n.delete(id);
      } else {
        n.add(id);
      }
      return n;
    });

  const doPreview = async () => {
    setErr(null);
    setMsg(null);
    try {
      setPreview(await exportPreview(Array.from(sel)));
    } catch (e) {
      setErr(financeErrorMessage(e));
    }
  };
  const refreshAll = () =>
    qc.invalidateQueries({
      predicate: (q) => String(q.queryKey[0]).startsWith("finance-"),
    });
  const doExport = async () => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const out = await exportSelection(flowDeps, flow, Array.from(sel));
      setFlow(out.state);
      if (out.exportedIds.length > 0) {
        // remove ONLY the rows that went into the created batch
        setSel(
          (cur) =>
            new Set(
              Array.from(cur).filter((id) => !out.exportedIds.includes(id)),
            ),
        );
        setPreview(null);
        await refreshAll();
      }
      setMsg(out.message);
      setErr(out.error);
    } finally {
      setBusy(false);
    }
  };
  const doRetryDownload = async (batchId: string) => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const out = await retryDownload(flowDeps, flow, batchId);
      setFlow(out.state);
      setMsg(out.message);
      setErr(out.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="finance-ap-page">
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Ready for export
            </h3>
            <p className="text-sm text-gray-500">
              Approved actuals not yet sent to Business Central. Each vendor
              invoice gets its own CRM invoice number (e.g. CRM-000123); lines
              with the same vendor and invoice number share it and become one
              invoice in BC. The vendor&apos;s invoice number goes into the line
              description.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={sel.size === 0 || busy}
              onClick={doPreview}
            >
              Preview CSV
            </Button>
            <Button
              size="sm"
              disabled={sel.size === 0 || busy}
              onClick={doExport}
              data-testid="finance-export-button"
            >
              {busy
                ? "Exporting…"
                : `Export ${sel.size || ""} to Business Central`}
            </Button>
          </div>
        </div>
        {flow.pending.length > 0 && (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="finance-pending-downloads"
          >
            <p className="font-medium">Created but not downloaded yet:</p>
            <ul className="mt-1 space-y-1">
              {flow.pending.map((p) => (
                <li key={p.batchId} className="flex items-center gap-3">
                  <span>
                    Batch {p.bcBatchId} · {p.ids.length} lines · {p.fileName}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => doRetryDownload(p.batchId)}
                    data-testid={`finance-download-pending-${p.batchId}`}
                  >
                    Download
                  </Button>
                </li>
              ))}
            </ul>
            <p className="mt-1 text-xs">
              These rows are already exported; downloading again gives the
              identical file. Exporting your current selection is a separate
              batch.
            </p>
          </div>
        )}
        {flow.unconfirmed && (
          <p
            className="rounded-md bg-amber-50 p-3 text-xs text-amber-900"
            data-testid="finance-unconfirmed-export"
          >
            The last export of {flow.unconfirmed.ids.length} rows did not
            confirm. Pressing Export with exactly those rows again is safe (it
            returns the same batch if it was created); check Export batches
            below.
          </p>
        )}
        {sel.size > 0 && (
          <p className="text-sm text-gray-700">
            {sel.size} selected · {money(total)}
          </p>
        )}
        {msg && (
          <p
            className="rounded-md bg-green-50 p-3 text-sm text-green-800"
            data-testid="finance-export-result"
          >
            {msg}
          </p>
        )}
        {err && (
          <p
            role="alert"
            className="rounded-md bg-red-50 p-3 text-sm text-red-800"
          >
            {err}
          </p>
        )}
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table
            className="min-w-full divide-y divide-gray-200 text-sm"
            data-testid="finance-ap-table"
          >
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={rows.length > 0 && sel.size === rows.length}
                    onChange={(e) =>
                      setSel(
                        e.target.checked
                          ? new Set(rows.map((r) => r.id))
                          : new Set(),
                      )
                    }
                  />
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">
                  Expense
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">
                  Vendor
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">
                  Invoice
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">
                  Due
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">
                  GL / dept / state
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-500">
                  Actual
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-500">
                  Variance
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-gray-500">
                    Nothing is ready for export.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${r.expense_no}`}
                      checked={sel.has(r.id)}
                      onChange={() => toggle(r.id)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="font-medium text-primary-600 hover:underline"
                      onClick={() => setOpenId(r.id)}
                    >
                      {r.expense_no}
                    </button>
                    <div className="max-w-xs truncate text-xs text-gray-500">
                      {r.description}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {payeeName(r.payee_id)}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {r.invoice_number} · {r.invoice_date}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {r.actual_due_date}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-700">
                    {r.gl_account_no} /{" "}
                    {lookupsQ.data?.departments.find(
                      (d) => d.id === r.department_id,
                    )?.bc_dimension_code ?? "?"}{" "}
                    / {r.state ?? "?"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {money(r.actual_amount)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {money(r.variance_amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {preview && (
          <div className="space-y-2" data-testid="finance-export-preview">
            {Object.keys(preview.problems).length > 0 && (
              <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
                These would block the export:
                <ul className="list-disc pl-5">
                  {Object.entries(preview.problems).map(([k, v]) => (
                    <li key={k}>
                      {k}: {v.join("; ")}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <pre className="max-h-64 overflow-auto rounded-md bg-gray-900 p-3 text-xs text-gray-100">
              {preview.csv}
            </pre>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h3 className="text-base font-semibold text-gray-900">
            Export batches
          </h3>
          <label className="text-sm text-gray-700">
            Find a CRM invoice number
            <input
              type="search"
              className="ml-2 w-44 rounded-md border border-gray-300 px-2 py-1 font-mono text-sm"
              placeholder="CRM-000123"
              value={docSearch}
              onChange={(e) => setDocSearch(e.target.value)}
              data-testid="finance-doc-search"
            />
          </label>
        </div>
        {docQuery.length >= 3 && (
          <div
            className="rounded-md border border-gray-200 p-3 text-sm"
            data-testid="finance-doc-search-results"
          >
            {docSearchQ.isLoading && (
              <p className="text-gray-500">Searching…</p>
            )}
            {docSearchQ.data && docSearchQ.data.length === 0 && (
              <p className="text-gray-500">No export line has that number.</p>
            )}
            <ul className="space-y-1">
              {(docSearchQ.data ?? []).map((i) => (
                <li key={i.id}>
                  <span className="font-mono">{i.bc_document_no}</span> · batch{" "}
                  {batchById.get(i.batch_id)?.bc_batch_id ?? "(older)"} line{" "}
                  {i.line_no} · {i.expense_no} · vendor invoice{" "}
                  {i.vendor_invoice_number}{" "}
                  <button
                    type="button"
                    className="text-primary-600 hover:underline"
                    onClick={() => setOpenId(i.expense_id)}
                  >
                    open expense
                  </button>
                  {i.voided_at ? (
                    <span className="ml-2 text-gray-500">
                      voided — {i.void_reason}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table
            className="min-w-full divide-y divide-gray-200 text-sm"
            data-testid="finance-batches-table"
          >
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-500">
                  BC batch
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">
                  CRM invoice numbers
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">
                  Created
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-500">
                  Lines
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-500">
                  Total
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">
                  File
                </th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(batchesQ.data ?? []).length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-gray-500">
                    No exports yet.
                  </td>
                </tr>
              )}
              {(batchesQ.data ?? []).map((b) => (
                <tr key={b.id}>
                  <td className="px-3 py-2 font-mono">
                    {b.bc_batch_id}
                    <span className="ml-2 text-xs text-gray-400">
                      {b.batch_no}
                    </span>
                  </td>
                  <td
                    className="px-3 py-2 font-mono text-xs text-gray-700"
                    data-testid={`finance-batch-docs-${b.id}`}
                  >
                    {documentRange(
                      (b.finance_exported_invoices ?? []).map(
                        (r) => r.bc_document_no,
                      ),
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {new Date(b.created_at).toLocaleString()} ·{" "}
                    {personName(people, b.created_by)}
                  </td>
                  <td className="px-3 py-2 text-right">{b.row_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {money(b.total_amount)}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {b.file_name}
                    <br />
                    <span className="font-mono">
                      sha256 {b.file_sha256.slice(0, 16)}…
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right space-x-2">
                    <button
                      type="button"
                      className="text-primary-600 hover:underline"
                      onClick={() =>
                        downloadBatch(b.id).catch((e) =>
                          setErr(financeErrorMessage(e)),
                        )
                      }
                    >
                      Download
                    </button>
                    <button
                      type="button"
                      className="text-primary-600 hover:underline"
                      onClick={() =>
                        setBatchOpen(batchOpen === b.id ? null : b.id)
                      }
                    >
                      {batchOpen === b.id ? "Hide lines" : "Lines"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {batchOpen && (
          <ul
            className="space-y-1 rounded-md border border-gray-200 p-3 text-sm"
            data-testid="finance-batch-items"
          >
            {(itemsQ.data ?? []).map((i) => (
              <li key={i.id}>
                Line {i.line_no}:{" "}
                {i.bc_document_no && (
                  <span className="font-mono">{i.bc_document_no} · </span>
                )}
                {i.expense_no} · vendor invoice {i.vendor_invoice_number} ·{" "}
                <button
                  type="button"
                  className="text-primary-600 hover:underline"
                  onClick={() => setOpenId(i.expense_id)}
                >
                  open expense
                </button>
                {i.voided_at ? (
                  <span className="ml-2 text-gray-500">
                    voided — {i.void_reason}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
      <ExpenseDetailModal
        expenseId={openId}
        onClose={() => setOpenId(null)}
        onChanged={() =>
          qc.invalidateQueries({
            predicate: (q) => String(q.queryKey[0]).startsWith("finance-"),
          })
        }
      />
    </div>
  );
}
